import React, { useState, useEffect, useRef } from 'react';
import { Search, Award, X } from 'lucide-react';
import { api } from '../services/api';

interface Patente {
  id: string;
  patente: string;
  abreviacao?: string;
  ordem: number;
  categoria: string;
}

interface PatenteSelectFieldProps {
  value?: string;
  onChange: (patenteId: string, patenteNome: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  label?: string;
  required?: boolean;
}

export function PatenteSelectField({
  value = '',
  onChange,
  placeholder = 'Digite o nome da patente...',
  disabled = false,
  error,
  label,
  required = false
}: PatenteSelectFieldProps) {
  const [patentes, setPatentes] = useState<Patente[]>([]);
  const [filteredPatentes, setFilteredPatentes] = useState<Patente[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatente, setSelectedPatente] = useState<Patente | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Carregar patentes na montagem do componente
  useEffect(() => {
    loadPatentes();
  }, []);

  // Encontrar patente selecionada baseada no value
  useEffect(() => {
    if (!Array.isArray(patentes)) return;

    if (value && patentes.length > 0) {
      const patente = patentes.find(p => p.id === value);
      if (patente) {
        setSelectedPatente(patente);
        setSearchTerm(patente.patente);
      }
    } else {
      setSelectedPatente(null);
      setSearchTerm('');
    }
  }, [value, patentes]);

  // Filtrar patentes baseado no termo de busca
  useEffect(() => {
    if (!Array.isArray(patentes)) return;

    if (searchTerm.trim()) {
      const filtered = patentes.filter(patente =>
        patente.patente.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (patente.abreviacao && patente.abreviacao.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredPatentes(filtered);
    } else {
      setFilteredPatentes(patentes.slice(0, 15)); // Mostrar apenas as primeiras 15
    }
  }, [searchTerm, patentes]);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPatentes = async () => {
    setLoading(true);
    try {
      const response = await api.get('/patentes');
      const data = response.data;
      // Garantir que sempre seja um array e ordenar por ordem hierárquica
      const validData = Array.isArray(data) ? data : [];
      const sortedPatentes = validData.sort((a: Patente, b: Patente) => a.ordem - b.ordem);
      setPatentes(sortedPatentes);
    } catch (error) {
      console.error('Erro ao carregar patentes:', error);
      setPatentes([]); // Em caso de erro, definir como array vazio
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);

    // Se limpar o campo, limpar seleção
    if (!newValue.trim()) {
      setSelectedPatente(null);
      onChange('', '');
    }
  };

  const handleSelectPatente = (patente: Patente) => {
    setSelectedPatente(patente);
    setSearchTerm(patente.patente);
    setIsOpen(false);
    onChange(patente.id, patente.patente);
  };

  const handleClearSelection = () => {
    setSelectedPatente(null);
    setSearchTerm('');
    setIsOpen(false);
    onChange('', '');
    inputRef.current?.focus();
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const getCategoriaColor = (categoria: string) => {
    switch (categoria.toLowerCase()) {
      case 'oficial':
        return 'text-blue-600 bg-blue-100';
      case 'praça':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            disabled={disabled || loading}
            className={`
              w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500
              ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'}
              ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
              ${loading ? 'cursor-wait' : ''}
            `}
          />

          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {selectedPatente ? (
              <button
                type="button"
                onClick={handleClearSelection}
                disabled={disabled}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <X size={16} />
              </button>
            ) : (
              <Search size={16} className="text-gray-400" />
            )}
          </div>
        </div>

        {/* Dropdown com patentes */}
        {isOpen && !disabled && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
          >
            {loading ? (
              <div className="px-3 py-2 text-center text-gray-500">
                Carregando patentes...
              </div>
            ) : filteredPatentes.length > 0 ? (
              filteredPatentes.map((patente) => (
                <button
                  key={patente.id}
                  type="button"
                  onClick={() => handleSelectPatente(patente)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none flex items-center justify-between"
                >
                  <div className="flex items-center space-x-2">
                    <Award size={16} className="text-gray-400" />
                    <div>
                      <span className="font-medium text-gray-900">{patente.patente}</span>
                      {patente.abreviacao && (
                        <span className="ml-2 text-sm text-gray-500">
                          ({patente.abreviacao})
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoriaColor(patente.categoria)}`}>
                      {patente.categoria}
                    </span>
                    <span className="text-xs text-gray-400">
                      #{patente.ordem}
                    </span>
                  </div>
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-center text-gray-500">
                {searchTerm.trim() ? 'Nenhuma patente encontrada' : 'Digite para buscar patentes'}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}
